import { legacyCsvTable } from './legacyImport'

export const HISTORIC_FIELDS = ['id', 'created', 'scheduled', 'first_name', 'last_name', 'email', 'phone',
  'bikes', 'assigned_mechanic', 'collection_drop_off', 'accepted_status', 'status', 'invoice_status',
  'invoice_items', 'historical_amount', 'currency', 'customer', 'booking_type', 'billing_type',
  'is_warranty_job', 'is_motability_job', 'is_leasing_job', 'invoice_number', 'invoice_created',
  'due_date', 'payment_taken_on', 'service_revenue', 'parts_revenue', 'free_text_items_revenue',
  'collection_fees', 'discounts', 'effective_tax_percent', 'total_inc_tax', 'total_exc_tax',
  'tax_amount', 'paid', 'outstanding', 'payment_transaction', 'status_booked_in',
  'status_bike_arrived', 'status_waiting_for_parts', 'status_waiting_for_customer_approval',
  'status_working_on', 'status_bike_ready', 'status_completed']
const ALIASES = { job_id: 'id', job_number: 'id', created_at: 'created', booked_in: 'scheduled',
  job_created: 'created', job_status: 'status', job_accepted_status: 'accepted_status',
  job_booking_type: 'booking_type',
  total: 'historical_amount', effective_tax: 'effective_tax_percent',
  customer_first_name: 'first_name', customer_last_name: 'last_name', customer_email: 'email',
  customer_phone: 'phone', mobile: 'phone', technician: 'assigned_mechanic',
  collection_dropoff: 'collection_drop_off', drop_off_collection_date: 'collection_drop_off',
  invoice_lines: 'invoice_items',
  invoice_line_items: 'invoice_items', line_items: 'invoice_items', invoice_total: 'historical_amount',
  total_amount: 'historical_amount', amount_charged: 'historical_amount', invoice_currency: 'currency' }
const clean = value => String(value ?? '').trim()
const absent = value => ["'-", '-', '[object object]', 'n/a', 'null'].includes(clean(value).toLowerCase())
const digits = value => clean(value).replace(/^(?:job[-_ ]*)/i, '')

export function historicalSourceKey(value) {
  const original = clean(value)
  const numeric = digits(original)
  return /^\d+$/.test(numeric) ? `numeric:${BigInt(numeric).toString()}` : `text:${original.toLowerCase()}`
}

const contact = value => clean(value).toLowerCase()
const phone = value => clean(value).replace(/\D/g, '')
const name = (first, last) => `${contact(first)} ${contact(last)}`.trim()

export const historicalOriginalCustomer = facts => clean(facts.customer)
  || [facts.first_name, facts.last_name].filter(Boolean).join(' ')

export function historicalCustomerCandidates(facts, customers) {
  const email = contact(facts.email)
  const number = phone(facts.phone)
  const sourceName = contact(facts.customer) || name(facts.first_name, facts.last_name)
  const contactMatches = customers.filter(customer =>
    (email && contact(customer.email) === email) || (number && phone(customer.phone) === number))
  const exactName = customer => name(customer.first_name, customer.last_name) === sourceName
  if (contactMatches.length) return {
    kind: contactMatches.length === 1 && exactName(contactMatches[0]) ? 'contact' : 'ambiguous',
    candidates: contactMatches,
  }
  const nameMatches = sourceName ? customers.filter(exactName) : []
  return { kind: nameMatches.length ? 'name_only' : 'none', candidates: nameMatches }
}

export function parseHistoricJobs(text) {
  const table = legacyCsvTable(text)
  const headers = table.headers.map(header => ALIASES[header] || header)
  if (!headers.includes('id') || !['created', 'scheduled', 'status'].some(header => headers.includes(header))) {
    return { rows: [], ignoredColumns: [], errors: ['Historical jobs need a source ID and a date or status field.'] }
  }
  if (!table.rows.length) return { rows: [], ignoredColumns: [], errors: ['Historical job file has no rows.'] }
  const names = headers.filter(Boolean)
  if (names.length !== new Set(names).size) return { rows: [], ignoredColumns: [], errors: ['Duplicate historical headings need review.'] }
  const ignoredColumns = names.filter(header => !HISTORIC_FIELDS.includes(header))
  const rows = table.rows.map((values, index) => {
    const raw = Object.fromEntries(headers.map((heading, column) => [heading, absent(values[column]) ? '' : clean(values[column])]))
    const sourceFields = Object.fromEntries(table.rawHeaders.map((heading, column) =>
      [heading || `Unnamed column ${column + 1}`, clean(values[column])]))
    const facts = Object.fromEntries(HISTORIC_FIELDS.map(field => [field, raw[field] || '']))
    const errors = []
    if (!facts.id || facts.id.length > 160) errors.push('Source job ID is required (160 characters maximum).')
    if (HISTORIC_FIELDS.some(field => facts[field].length > (field === 'invoice_items' ? 2000 : 500))) {
      errors.push('A historical field is too long.')
    }
    if (values.length > headers.length && values.slice(headers.length).some(clean)) errors.push('Extra unheaded cells need review.')
    return { rowNumber: table.rowNumbers?.[index] || index + 2, raw, sourceFields, facts, errors }
  })
  const ids = new Set()
  rows.forEach(row => {
    const key = historicalSourceKey(row.facts.id)
    if (key && ids.has(key)) row.errors.push('Source job ID is repeated in this file.')
    ids.add(key)
  })
  return { rows, ignoredColumns, errors: [] }
}

export function reviewHistoricJobs(rows, existingRecords, skippedNumbers = []) {
  const skipped = new Set(skippedNumbers)
  return rows.map(row => {
    const existing = existingRecords.find(record => historicalSourceKey(record.source_reference)
      === historicalSourceKey(row.facts.id))
    const conflict = existing && HISTORIC_FIELDS.filter(field => field !== 'id')
      .some(field => (existing.facts?.[field] || '') !== row.facts[field])
    const errors = [...row.errors]
    if (conflict && !skipped.has(row.rowNumber)) errors.push('Source ID conflicts with existing history; explicitly Skip or resolve separately.')
    return { ...row, existing, conflict, skip: skipped.has(row.rowNumber), errors,
      action: errors.length ? 'blocked' : skipped.has(row.rowNumber) ? 'skip' : existing ? 'reuse' : 'create' }
  })
}

export function historicJobPayload(rows) {
  return rows.map(row => ({ row_number: row.rowNumber, decision: row.skip ? 'skip' : 'import', ...row.facts }))
}
